<script setup lang="ts">
import { onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute, useRouter } from "vue-router";

import { api, ApiError } from "@/api/client";

// One-click shortcut: open a fresh shell session on the agent and jump
// straight into the terminal. On failure (e.g. agent offline) we show the
// error with a link back to the agent's detail page rather than dead-end.
const { t } = useI18n();
const route = useRoute();
const router = useRouter();

const agentId = route.params.agentId as string;
const error = ref<string | null>(null);

onMounted(async () => {
  try {
    const session = await api.sessions.start(agentId, { engine: "shell" });
    await router.replace({ name: "session", params: { sessionId: session.id } });
  } catch (err) {
    error.value =
      err instanceof ApiError ? `${err.code} (HTTP ${err.status})` : (err as Error).message;
  }
});
</script>

<template>
  <section class="shell-launch">
    <template v-if="error">
      <p class="admin-error">{{ error }}</p>
      <RouterLink :to="{ name: 'agent', params: { agentId } }"
        >← {{ t("agent.back_detail") }}</RouterLink
      >
    </template>
    <p v-else class="shell-launch__pending" role="status" aria-live="polite">
      <span class="spinner" aria-hidden="true"></span> {{ t("agent.starting_shell") }}
    </p>
  </section>
</template>

<style scoped>
.shell-launch {
  max-width: 32rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}
.shell-launch__pending {
  display: flex;
  align-items: center;
  gap: 0.55rem;
  color: var(--color-fg-dim);
}
</style>
